import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Text } from 'office-ui-fabric-react/lib/Text';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import SearchBar from '../../components/SearchBar';
import ProductsList from '../../components/ProductsList';
import { fetchProducts } from '../../actions/products';
import { fetchPartners } from '../../actions/partners';
import { search } from '../../actions/search';
import Pagination from '../../components/Pagination';
import styles from './ProductListTab.style';

const ProductListTab = () => {
  const dispatch = useDispatch();
  const [sortAscending, setSortAscending] = useState(false);
  const [sortColumn, setSortColumn] = useState();
  const [loading, setLoading] = useState(true);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [totalCount, setTotalCount] = React.useState(0);
  const products = useSelector(state =>
    get(state, 'pagination.products.results', [])
      .map(tokenId => get(state, `entities.products.${tokenId}`))
      .sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a[sortColumn] > b[sortColumn] ? (sortAscending ? -1 : 1) : sortAscending ? 1 : -1,
      ),
  );
  const partners = useSelector(state => get(state, `entities.partners`, []));
  const searchResults = useSelector(state =>
    Object.values(get(state, 'search.results.products', {})),
  );

  useEffect(() => {
    async function loadProducts() {
      const response = await dispatch(fetchProducts());
      setTotalCount(get(response, `meta.totalCount`, 0));
      setLoading(false);
    }
    loadProducts();
    dispatch(fetchPartners());
  }, [dispatch]);

  const handleColumnClick = (event, column) => {
    setSortAscending(!sortAscending);
    setSortColumn(column.fieldName);
  };

  const handleClear = () => {
    setShowSearchResults(false);
  };

  const handleSearch = async query => {
    if (!query) {
      setShowSearchResults(false);
      return;
    }
    setShowSearchResults(true);
    setLoading(true);
    await dispatch(search(['products'], query));
    setLoading(false);
  };

  const handlePageChange = paginationData => {
    dispatch(
      fetchProducts({
        start: paginationData.currentPageIndex * paginationData.pageLimit,
        count: paginationData.pageLimit,
      }),
    );
  };

  const baseColumns = [
    {
      key: 'icon',
      name: 'icon',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'icon',
      iconName: 'Tag',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 14,
      maxWidth: 14,
      columnActionsMode: 0,
      onRender: () => {
        return (
          <div className={styles.iconColumn}>
            <FontIcon iconName="Tag" ariaLabel="Tag" className={styles.detailRowIconColor} />
          </div>
        );
      },
    },
    {
      key: 'name',
      name: 'Product Name',
      fieldName: 'name',
      isSorted: sortColumn === 'name',
      isSortedDescending: !sortAscending,
      minWidth: 320,
      maxWidth: 320,
      isResizable: true,
      onColumnClick: handleColumnClick,
    },
    {
      key: 'tokenId',
      name: 'Token ID',
      fieldName: 'tokenId',
      isSorted: sortColumn === 'tokenId',
      isSortedDescending: !sortAscending,
      minWidth: 185,
      maxWidth: 185,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: ({ tokenId }) => {
        return `#${tokenId}`;
      },
    },
  ];

  const ownerColumn = [
    {
      key: 'owner',
      name: 'Owner',
      fieldName: 'owner',
      isSorted: sortColumn === 'owner',
      isSortedDescending: !sortAscending,
      minWidth: 150,
      maxWidth: 150,
      isResizable: true,
      onColumnClick: handleColumnClick,
      onRender: ({ owner }) => {
        return get(partners, `${owner}.name`);
      },
    },
  ];

  const columnsWithPartner = [...baseColumns, ...ownerColumn];

  const displayPartnerColumn = Object.values(partners).length > 1;

  return (
    <>
      <SearchBar
        placeholder="Search products"
        ariaLabel="Search"
        underlined
        onChange={handleSearch} // when you type
        onClear={handleClear} // when you click X
        data-testid="search-input"
        className={styles.search}
      />

      {showSearchResults && !loading && (
        <div className={styles.resultsMessage}>
          <Text as="h4" block>
            {searchResults.length || 'No'} Results
          </Text>
          {searchResults.length === 0 && (
            <Text as="p" block className={styles.noResultsSentence}>
              Try searching by product name, token ID, or partner name
            </Text>
          )}
        </div>
      )}

      {(() => {
        if (loading) {
          return (
            <Module>
              <ModuleContentList>
                <ProductsList
                  columns={displayPartnerColumn ? columnsWithPartner : baseColumns}
                  items={[]}
                  shimmered
                  enableShimmer
                  noBorderBottom
                />
              </ModuleContentList>
            </Module>
          );
        }

        if (showSearchResults && searchResults.length === 0) {
          return '';
        }

        return (
          <Module>
            <ModuleContentList>
              <ProductsList
                columns={displayPartnerColumn ? columnsWithPartner : baseColumns}
                items={showSearchResults ? searchResults : products}
                shimmered
                enableShimmer={loading}
                noBorderBottom
              />
            </ModuleContentList>
            <Pagination
              totalRecords={showSearchResults ? searchResults.length : totalCount}
              handlePageChange={handlePageChange}
            />
          </Module>
        );
      })()}
    </>
  );
};

export default ProductListTab;
